import { IconNames } from '@blueprintjs/icons';
import { Table2 as BPTable, Cell, Column, RenderMode, SelectionModes } from '@blueprintjs/table';
import { sortAsc, sortDateAsc } from '@strata/helper-functions';
import { TextInput } from '@strata/react-inputs';
import { Icon } from '@strata/react-status-icons';
import { Tooltip } from '@strata/react-tooltip';
import classNames from 'classnames';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Link } from 'react-router';

const headerCell = (name, sort, index) => (
    <>
        {sort?.colIndex === index && (
            <Icon icon={sort.sortAsc ? IconNames.CHEVRON_UP : IconNames.CHEVRON_DOWN} />
        )}
        {name}
    </>
);

export const Table = ({
    columnNames,
    columnWidths = undefined,
    tableData = undefined,
    editPath = undefined,
    keyFunction = undefined,
    disabled = false,
    rowClassName = undefined,
    defaultRowHeight = undefined,
    initialSort = undefined,
}) => {
    const [searchText, setSearchText] = useState('');
    const realKeyFunction = keyFunction || ((array) => array[0]);
    const renderCell = (data, rowIndex, columnIndex) => {
        const rowData = data[rowIndex];
        const cellData = rowData[columnIndex];
        return (
            <Cell className={rowClassName?.(rowData)}>
                <Tooltip
                    className={classNames('sds_table-cell__text', {
                        'sds_table-cell__text--disabled': disabled || !editPath,
                    })}
                    content={typeof cellData === 'string' ? cellData : undefined}
                >
                    <Link to={`${editPath}/${realKeyFunction(rowData)}`}>{cellData}</Link>
                </Tooltip>
            </Cell>
        );
    };

    const [sort, setSort] = useState(initialSort);
    const updateSort = (colIndex) => {
        if (colIndex !== undefined) {
            setSort((prev) => ({
                colIndex,
                sortAsc: prev?.colIndex === colIndex ? !prev.sortAsc : true,
            }));
        }
    };

    // basic filter for data based on search text (case-insensitive)
    const filterData = (data, text) =>
        !text
            ? data
            : data.filter((row) =>
                  Object.values(row).some((value) =>
                      value?.toString().toLowerCase().includes(text?.toLowerCase()),
                  ),
              );

    if (tableData?.length > 0) {
        const sortedData = sort
            ? tableData.sort((a, b) => {
                  const leftVal = sort.sortAsc ? a[sort.colIndex] : b[sort.colIndex];
                  const rightVal = sort.sortAsc ? b[sort.colIndex] : a[sort.colIndex];

                  return dayjs(leftVal).isValid() || dayjs(rightVal).isValid()
                      ? sortDateAsc(leftVal, rightVal)
                      : sortAsc(JSON.stringify(leftVal), JSON.stringify(rightVal));
              })
            : tableData;
        const filteredData = filterData(sortedData, searchText);
        return (
            <div className="lucent_table">
                <TextInput
                    id="tableSearch"
                    name="tableSearch"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    type="text"
                    placeholderText="Search..."
                />
                <BPTable
                    key={`${filteredData.length}${sort?.colIndex}${sort?.sortAsc}`} // Fix for https://github.com/palantir/blueprint/issues/3757
                    numRows={filteredData.length}
                    columnWidths={columnWidths}
                    renderMode={RenderMode.BATCH}
                    selectionModes={SelectionModes.COLUMNS_ONLY}
                    enableMultipleSelection={false}
                    defaultRowHeight={defaultRowHeight}
                    onSelection={(sel) => updateSort(sel[0]?.cols?.[0])}
                >
                    {columnNames.map((col, index) => (
                        <Column
                            key={col}
                            name={col}
                            nameRenderer={(name) => headerCell(name, sort, index)}
                            cellRenderer={(r) => renderCell(filteredData, r, index)}
                        />
                    ))}
                </BPTable>
            </div>
        );
    }

    return null;
};

Table.propTypes = {
    columnNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    columnWidths: PropTypes.arrayOf(PropTypes.number),
    tableData: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.node)),
    editPath: PropTypes.string,
    keyFunction: PropTypes.func,
    disabled: PropTypes.bool,
    rowClassName: PropTypes.func,
    defaultRowHeight: PropTypes.number,
    initialSort: PropTypes.shape({
        colIndex: PropTypes.number.isRequired,
        sortAsc: PropTypes.bool.isRequired,
    }),
};
